import {openDB} from 'idb'
import React, {useContext, useState} from 'react'
import {Button} from 'react-bootstrap'
import {formatDateToDMY} from 'src/app/helpers/dateFunctions'
import {KTIcon} from '../../../../_metronic/helpers'
import {downloadFileS3} from '../../../services/files'
import {enviarWNomina} from '../../../services/wnomina'
import {showInfoHtml} from '../../../shared/Alerts'
import {useGlobalOverlayLoading} from '../../../shared/hooks/useGlobalOverlayLoading'
import {IconName} from '../../../shared/KTIIconKeys'
import {CustomTooltip} from '../../generales_component/CustomTooltip'
import {GlobalModal} from '../../generales_component/modal/GloblaModal'
import {PdfLoadingSpinner} from '../../generales_component/PdfLoadingSpinner'
import {Doc} from '../../generales_component/preview-document-modal/PreviewDocument'
import {PreviewDocumentModal} from '../../generales_component/preview-document-modal/PreviewDocumentModal'
import {TableBusiness} from '../../generales_component/table/TableBusiness'
import {orderStrings} from '../../generales_functions/ordering'
import {AltaContext} from './AltaContext'

const statusToSend = ['pendiente', 'observado', 'enviado']
const states: Record<string, string> = {
  aprobadoConPendiente: 'Aprobado con pendiente',
  aprobadoPorAdmin: 'Aprobado',
  documentosPendientesDeFirma: 'Documentos pendientes de firma',
  documentosEnProcesoDeFirma: 'Documentos en proceso de firma',
  documentosFirmados: 'Documentos firmados',
  procesoCerrado: 'Proceso cerrado',
  procesoCerradoConPendiente: 'Proceso cerrado con pendiente',
}

type HeaderEntry = {
  key: string
  value: string
  icon?: IconName
  iconClass?: string
  onSort?: (key: string) => void
}

type SortState = {
  key: string
  order: 'asc' | 'desc'
}

async function addIndexDbData(resumenPorUsuario: any, response: any) {
  try {
    const db = await openDB('wd-front-db', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('nominaResumen')) {
          db.createObjectStore('nominaResumen', {keyPath: 'id', autoIncrement: true})
        }
      },
    })

    await db.add('nominaResumen', {
      fecha: new Date().toISOString(),
      resumenPorUsuario,
      responseData: response,
    })
  } catch (err) {
    console.log('error saving to indexdb')
  }
}

function buildUserInsertStatus(
  responseDefaultData: any,
  responseDirectionData: any,
  responseContratosData: any,
  responseFamiliaData: any,
  responseCCOSData: any
) {
  const usersInserted = responseDefaultData?.usersInserted || []
  const directionInserted = responseDirectionData?.inserted || []
  const directionRejected = responseDirectionData?.rejected || []
  const contratosInserted = responseContratosData?.inserted || []
  const contratosRejected = responseContratosData?.rejected || []
  const cecosInserted = responseCCOSData?.inserted || []
  const cecosRejected = responseCCOSData?.rejected || []

  const usersByDni: Record<string, any> = {}

  for (const user of usersInserted) {
    const dni = user.dni
    usersByDni[dni] = {
      dni,
      fullname: user.fullname,
      defaultInserted: 'inserted',
      directionStatus: 'not_sent',
      directionError: null,
      contratoStatus: 'not_sent',
      contratoError: null,
      familiaStatus: 'not_sent',
      familiaError: null,
      cecosStatus: 'not_sent',
      cecosError: null,
    }
  }

  for (const user of directionInserted) {
    const dni = user.Codigo_trabajador
    if (usersByDni[dni]) {
      usersByDni[dni].directionStatus = 'inserted'
    }
  }

  for (const user of directionRejected) {
    const dni = user.dni
    if (usersByDni[dni]) {
      usersByDni[dni].directionStatus = 'rejected'
      usersByDni[dni].directionError = user.error || null
    }
  }

  for (const user of contratosInserted) {
    const dni = user.CodTrab
    if (usersByDni[dni]) {
      usersByDni[dni].contratoStatus = 'inserted'
    }
  }

  for (const user of contratosRejected) {
    const dni = user.dni
    if (usersByDni[dni]) {
      usersByDni[dni].contratoStatus = 'rejected'
      usersByDni[dni].contratoError = user.error || null
    }
  }

  const familiaPorDni: Record<
    string,
    {inserted: number; rejected: {error: string; dni: string; dniFam: string}[]}
  > = {}

  for (const familiaResp of responseFamiliaData ?? []) {
    const inserted = familiaResp?.inserted || []
    const rejected = familiaResp?.rejected || []

    for (const fam of inserted) {
      const dni = fam.Codigo_trabajador
      if (!familiaPorDni[dni]) {
        familiaPorDni[dni] = {inserted: 0, rejected: []}
      }
      familiaPorDni[dni].inserted += 1
    }

    for (const fam of rejected) {
      const dni = fam.dni
      if (!familiaPorDni[dni]) {
        familiaPorDni[dni] = {inserted: 0, rejected: []}
      }
      familiaPorDni[dni].rejected.push({error: fam.error ?? null, dni: fam.dni, dniFam: fam.dniFam})
    }
  }

  for (const dni in familiaPorDni) {
    const data = familiaPorDni[dni]
    const total = data.inserted + data.rejected.length

    if (!usersByDni[dni]) continue

    if (data.inserted === total) {
      usersByDni[dni].familiaStatus = 'inserted'
    } else if (data.rejected.length === total) {
      usersByDni[dni].familiaStatus = 'rejected'
      usersByDni[dni].familiaError = data.rejected[0].error
    } else {
      usersByDni[dni].familiaStatus = 'partial'
      usersByDni[dni].familiaError = data.rejected[0].error
    }
  }

  const cecosPorDni: Record<string, {inserted: number; rejected: {error: string; dni: string}[]}> =
    {}

  for (const user of cecosInserted) {
    const dni = user.CodTrab
    if (!cecosPorDni[dni]) {
      cecosPorDni[dni] = {inserted: 0, rejected: []}
    }
    cecosPorDni[dni].inserted += 1
  }

  for (const user of cecosRejected) {
    const dni = user.dni
    if (!cecosPorDni[dni]) {
      cecosPorDni[dni] = {inserted: 0, rejected: []}
    }
    cecosPorDni[dni].rejected.push({error: user.error ?? null, dni: user.dni})
  }

  // Determinar el estado final de cecos por usuario
  for (const dni in cecosPorDni) {
    const data = cecosPorDni[dni]
    const total = data.inserted + data.rejected.length

    if (!usersByDni[dni]) continue

    if (data.inserted === total) {
      usersByDni[dni].cecosStatus = 'inserted'
    } else if (data.rejected.length === total) {
      usersByDni[dni].cecosStatus = 'rejected'
      usersByDni[dni].cecosError = data.rejected[0]?.error
    } else {
      usersByDni[dni].cecosStatus = 'partial'
      usersByDni[dni].cecosError = data.rejected[0]?.error
    }
  }

  return Object.values(usersByDni)
}

const AltaBody: React.FC = () => {
  const {
    setFichasColaboradores,
    fichasColaboradores,
    loading,
    usersSelected,
    setUsersSelected,
    handleOpenFieldModal,
    fieldSelected,
    status,
    processId,
    fetchPartnersData,
  } = useContext(AltaContext)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [sort, setSort] = useState<SortState | null>(null)
  const [ficha, setFicha] = useState<Doc>({
    uri: '',
    fileType: '',
    fileName: '',
  })
  const {setGlobalOverlayLoading} = useGlobalOverlayLoading()

  const getIdRegistroFicha = (dni: string) => {
    const ficha = fichasColaboradores.find((ficha) => ficha.id_user_creador.dni === dni)
    return {
      registroFicha: ficha?._id,
      fullname: ficha?.id_user_creador.nombre,
    }
  }

  const [openTableModal, setOpenTableModal] = useState(false)
  const [tables, setTables] = useState<any[]>([])
  const [icon, setIcon] = useState<'success' | 'warning'>('success')

  const handleSendNomina = async () => {
    const usersFilter = Object.keys(usersSelected).filter((key) => usersSelected[key])
    const toSend = usersFilter.map((user) => ({dni: user, ...getIdRegistroFicha(user)}))

    try {
      setGlobalOverlayLoading(true)
      const response = await enviarWNomina({
        users: toSend,
        processId,
      })

      const resumenPorUsuario = buildUserInsertStatus(
        response?.data?.responseDefaultData,
        response?.data?.responseDirectionData,
        response?.data?.responseContratosData,
        response?.data?.responseFamilyData,
        response?.data?.responseCCOSData
      )

      await addIndexDbData(resumenPorUsuario, response)

      console.log(resumenPorUsuario)

      await fetchPartnersData()

      const usersNotSent = response?.data?.responseDefaultData?.usersNotSent ?? []
      const usersInserted = response?.data?.responseDefaultData?.usersInserted ?? []
      const usersRejected = response?.data?.responseDefaultData?.usersRejected ?? []

      const tableNotSent = {
        title: 'Los siguientes colaboradores no cumplen con los campos obligatorios',
        headers: [
          {key: 'dni', label: 'Nro de documento'},
          {key: 'fullname', label: 'Nombres y apellidos'},
          {key: 'requiredFields', label: 'Motivo'},
        ],
        data: usersNotSent?.map((user: Record<string, string>) => ({
          dni: user.dni,
          requiredFields: user.requiredFields,
          fullname: user.fullname,
        })),
      }

      const tableInserted = {
        title: 'Los siguientes colaboradores fueron dados de alta en Workin Nomina',
        headers: [
          {key: 'dni', label: 'Nro de documento'},
          {key: 'fullname', label: 'Nombres y apellidos'},
          // {key: 'family', label: 'Familia'},
          // {key: 'direction', label: 'Direccion'},
        ],
        data: usersInserted?.map((user: any) => ({
          dni: user.dni,
          fullname: user.fullname,
          // family: insertedFamily.includes(user) ? 'OK' : 'NO ENVIADO',
          // direction: insertedDirection.includes(user) ? 'OK' : 'NO ENVIADO',
        })),
      }

      const tableRejected = {
        id: 'Los siguientes colaboradores tienen observaciones',
        headers: [
          {key: 'dni', label: 'Nro de documento'},
          {key: 'fullname', label: 'Nombres y apellidos'},
          {key: 'error', label: 'Observacion'},
        ],
        data: usersRejected?.map((user: Record<string, string>) => ({
          dni: user.dni,
          fullname: user.fullname,
          error: user.error,
        })),
      }

      const statusReadable = {
        inserted: 'OK',
        not_sent: 'NE',
        rejected: 'RJ',
        partial: 'PC',
      }

      type statusType = 'inserted' | 'not_sent' | 'rejected'

      const resumeAPIs = {
        id: 'Resumen de envio APIs',
        headers: [
          {key: 'dni', label: 'Nro de documento'},
          {key: 'fullname', label: 'Nombres y apellidos'},
          {key: 'defaultInserted', label: 'Default'},
          {key: 'directionStatus', label: 'Direccion'},
          {key: 'contratoStatus', label: 'Contrato'},
          {key: 'familiaStatus', label: 'Familias'},
        ],
        data: resumenPorUsuario.map((rpu) => ({
          ...rpu,
          defaultInserted: statusReadable[rpu.defaultInserted as statusType] ?? rpu.defaultInserted,
          directionStatus: statusReadable[rpu.directionStatus as statusType] ?? rpu.directionStatus,
          contratoStatus: statusReadable[rpu.contratoStatus as statusType] ?? rpu.contratoStatus,
          familiaStatus: statusReadable[rpu.familiaStatus as statusType] ?? rpu.familiaStatus,
        })),
      }

      if (!response.success && response.internCode === 'WNOMINA_001') {
        throw new Error(response.message)
      }

      const icon =
        usersNotSent?.length === 0 && usersRejected?.length === 0 && response.success
          ? 'success'
          : 'warning'
      setIcon(icon)
      setOpenTableModal(true)
      setTables([tableNotSent, tableRejected, tableInserted, resumeAPIs])
    } catch (error) {
      const ex = error as Error
      showInfoHtml('error', 'Hubo un error al procesar la informacion', ex.message)
    } finally {
      setGlobalOverlayLoading(false)
    }
  }

  const handleCheckAll = (e: any) => {
    const copy = Object.assign({}, usersSelected)
    for (const key in copy) {
      const found = fichasColaboradores.find((colab) => colab.id_user_creador.dni === key)
      const criteria = found?.actualizable === 'Y' && found?.estado === 'aprobadoConPendiente'
      copy[key] = criteria ? false : e.target.checked
    }

    setUsersSelected(copy)
  }

  const handleCheckOne = (dni: string, checked: boolean) => {
    setUsersSelected((prev) => ({...prev, [dni]: checked}))
  }

  const handleViewFicha = async (ficha: Record<string, any>) => {
    // navigate(`/seguimiento/colaboradores/view/${ficha}`)
    const partes = ficha?.filename?.split('.') as string[]
    const extension = partes?.pop() ?? ''

    setGlobalOverlayLoading(true)
    const response = await downloadFileS3(ficha?.keyname, extension)
    setGlobalOverlayLoading(false)

    setFicha({
      uri: response.data,
      fileType: extension.toLocaleLowerCase(),
      fileName: ficha?.filename,
      keyName: ficha?.keyname,
    })
    setIsModalOpen(true)
  }

  const handleSort = (key: string, type: 'string' | 'date' | 'number') => {
    const exist = sort != null && sort.key === key
    const order = exist ? (sort.order === 'asc' ? 'desc' : 'asc') : 'asc'
    setSort({key, order})

    if (type === 'string') {
      setFichasColaboradores(orderStrings(fichasColaboradores, {key, order}))
      return
    }

    if (type === 'date') {
      setFichasColaboradores(orderStrings(fichasColaboradores, {key, order}))
      return
    }

    if (type === 'number') {
      return
    }
  }

  // const fieldSelectedLength = Object.entries(fieldSelected).reduce(
  //   (acc: number, [_, object]) => (acc += Object.keys(object).length),
  //   0
  // )
  const disabledButton = Object.values(usersSelected).every((value) => !value)
  const countUsers = Object.values(usersSelected).filter((value) => value).length
  const message = countUsers > 1 ? `${countUsers} usuarios` : '1 usuario'
  const pendingHeaders: HeaderEntry[] = [
    {
      key: 'dni',
      value: 'Nro. de documento',
      // icon:
      //   sort?.key === 'dni' ? (sort.order === 'asc' ? 'arrow-down' : 'arrow-up') : 'arrow-up-down',
      // iconClass: 'text-light',
      // onSort: (key: string) => handleSort(key, 'string'),
    },
    {
      key: 'fecha_ingreso',
      value: 'Fecha de ingreso',
      // icon:
      //   sort?.key === 'fecha_ingreso'
      //     ? sort.order === 'asc'
      //       ? 'arrow-down'
      //       : 'arrow-up'
      //     : 'arrow-up-down',
      // iconClass: 'text-light',
      // onSort: (key: string) => handleSort(key, 'date'),
    },
    {
      key: 'nombre',
      value: 'Nombres y apellidos',
      // icon:
      //   sort?.key === 'nombre'
      //     ? sort.order === 'asc'
      //       ? 'arrow-down'
      //       : 'arrow-up'
      //     : 'arrow-up-down',
      // iconClass: 'text-light',
      // onSort: (key: string) => handleSort(key, 'string'),
    },
    {key: 'estado', value: 'Estado OnBoarding'},
    {key: 'estadoEnvioWNomina', value: 'Estado envio'},
    {key: 'ver', value: 'Ver ficha'},
  ]

  const observedHeaders = [...pendingHeaders, {key: 'observacionWNomina', value: 'Observaciones'}]

  if (loading) {
    return (
      <div style={{minHeight: '100vh', maxHeight: '100vh'}}>
        <PdfLoadingSpinner />
      </div>
    )
  }

  return (
    <>
      <div className='table-responsive mt-5' style={{minHeight: '100vh', maxHeight: '100vh'}}>
        {statusToSend.includes(status) && (
          <div className='d-flex gap-3 justify-content-end mb-3'>
            {/* <Button onClick={handleOpenFieldModal}>Exportar {fieldSelectedLength} campos</Button> */}
            {/* {fichasColaboradores.length > 0 && (
              <OverlayTrigger
                overlay={
                  disabledButton ? (
                    <Tooltip id='tooltip'>
                      Seleccione campos y/o usuarios para enviar a nomina
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              >
                <div> */}
            <Button
              className='btn btn-success'
              onClick={handleSendNomina}
              disabled={disabledButton}
            >
              Enviar {countUsers === 0 ? '' : `${message} `} a Workin Nomina
            </Button>
            {/* </div>
              </OverlayTrigger>
            )} */}
          </div>
        )}
        {fichasColaboradores.length === 0 ? (
          <div className='d-flex justify-content-center'>No se encontraron colaboradores</div>
        ) : (
          <TableBusiness
            checkbox={{
              include: statusToSend.includes(status),
              onChange: handleCheckAll,
            }}
            headers={status === 'observado' ? observedHeaders : pendingHeaders}
          >
            {fichasColaboradores.map((colabs) => (
              <tr
                key={colabs._id}
                onClick={
                  statusToSend.includes(status) &&
                  !(colabs.actualizable === 'Y' && colabs.estado === 'aprobadoConPendiente')
                    ? () =>
                        handleCheckOne(
                          colabs.id_user_creador.dni,
                          !(usersSelected[colabs.id_user_creador.dni] ?? false)
                        )
                    : undefined
                }
              >
                {statusToSend.includes(status) && (
                  <td className='text-start' style={{paddingBlock: '5px', paddingLeft: '5px'}}>
                    <div className='form-check form-check-custom form-check-solid form-check-sm m-2'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={usersSelected[colabs.id_user_creador.dni] ?? false}
                        disabled={
                          colabs.actualizable === 'Y' && colabs.estado === 'aprobadoConPendiente'
                        }
                        id={colabs.id_user_creador.dni}
                        onChange={(e) =>
                          handleCheckOne(colabs.id_user_creador.dni, e.target.checked)
                        }
                      />
                    </div>
                  </td>
                )}
                <td className='text-start min-w-100px'>{colabs.id_user_creador.dni}</td>
                <td className='text-start min-w-100px'>{formatDateToDMY(colabs.fecha_ingreso)}</td>
                <td className='text-start min-w-100px'>{colabs.id_user_creador.nombre}</td>
                <td className='text-start min-w-100px'>
                  {states[colabs.estado].toUpperCase() ?? ''}
                </td>
                <td className='text-start min-w-100px'>
                  {colabs.estadoEnvioWNomina.toUpperCase()}
                </td>
                <td className='text-start min-w-100px'>
                  <button
                    className='p-0 btn'
                    style={{margin: '0 25%'}}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation()
                      handleViewFicha(colabs.pdf)
                    }}
                  >
                    <KTIcon iconName='eye' className='fs-4' />
                  </button>
                </td>
                {status === 'observado' && (
                  <td className='text-start min-w-100px p-2 text-center'>
                    <CustomTooltip
                      variant='light'
                      className='btn-not-hover bg-transparent text-black text-hover-black m-0 p-0'
                      icon='information-2'
                      iconClass='text-warning fs-4'
                      placement='left'
                    >
                      {colabs.observacionWNomina}
                    </CustomTooltip>
                  </td>
                )}
              </tr>
            ))}
          </TableBusiness>
        )}
      </div>
      <PreviewDocumentModal
        isPreviewDocumentModalOpen={isModalOpen}
        setIsPreviewDocumentModalOpen={setIsModalOpen}
        title=''
        documentos={[ficha]}
      />
      <GlobalModal
        tables={tables}
        handleClose={() => {
          setOpenTableModal(false)
        }}
        show={openTableModal}
        icon={icon}
      />
    </>
  )
}

export {AltaBody}
